/**
 * Analytics Service
 * 
 * Handles GA4 and Meta Pixel tracking with consent management.
 * No PII is ever sent to analytics platforms.
 */

import { 
  ANALYTICS_CONFIG, 
  isGA4Enabled, 
  isMetaPixelEnabled, 
  hasAcceptedTracking 
} from '../config/analytics';

let ga4Initialized = false;
let metaPixelInitialized = false;

/**
 * Initialize GA4 (Google Analytics 4)
 * Only initializes if enabled, configured, and consent given
 */
export const initializeGA4 = () => {
  if (ga4Initialized || !isGA4Enabled() || !hasAcceptedTracking()) {
    return;
  }

  const measurementId = ANALYTICS_CONFIG.GA4_MEASUREMENT_ID;
  
  // Load gtag.js
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    // Privacy settings - no PII collection
    anonymize_ip: true,
    send_page_view: false, // We'll send manually for SPA
  });

  ga4Initialized = true;
  console.log('[Analytics] GA4 initialized');
};

/**
 * Initialize Meta Pixel
 * Only initializes if enabled, configured, and consent given
 */
export const initializeMetaPixel = () => {
  if (metaPixelInitialized || !isMetaPixelEnabled() || !hasAcceptedTracking()) {
    return;
  }

  const pixelId = ANALYTICS_CONFIG.META_PIXEL_ID;

  // Meta Pixel base code
  !function(f,b,e,v,n,t,s) {
    if(f.fbq)return;
    n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;
    n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];
    t=b.createElement(e);t.async=!0;
    t.src=v;
    s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s);
  }(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');

  window.fbq('init', pixelId);
  // Don't auto-track PageView - we'll do it manually for SPA

  metaPixelInitialized = true;
  console.log('[Analytics] Meta Pixel initialized');
};

/**
 * Initialize all analytics
 */
export const initializeAnalytics = () => {
  if (!hasAcceptedTracking()) {
    console.log('[Analytics] Tracking not initialized - consent not given');
    return;
  }

  initializeGA4();
  initializeMetaPixel();
};

/**
 * Track page view (SPA-safe)
 * @param {string} path - The current path
 * @param {string} title - The page title
 */
export const trackPageView = (path, title) => {
  if (!hasAcceptedTracking()) return;

  // GA4 page view
  if (isGA4Enabled() && window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title,
      page_location: window.location.href,
    });
    console.log('[Analytics] GA4 page_view:', path);
  }

  // Meta Pixel page view
  if (isMetaPixelEnabled() && window.fbq) {
    window.fbq('track', 'PageView');
    console.log('[Analytics] Meta PageView:', path);
  }
};

/**
 * Track Lead event (form submission success)
 * Called ONLY after successful form submission
 * NO PII is sent - only the event type and service category
 * 
 * @param {Object} options
 * @param {string} options.service - Service type (e.g., 'record_suspension')
 */
export const trackLead = ({ service }) => {
  if (!hasAcceptedTracking()) return;

  // Sanitized data - NO PII
  const eventData = {
    content_category: service || 'eligibility_assessment',
    content_name: 'Eligibility Form Submission',
  };

  // GA4 Lead event
  if (isGA4Enabled() && window.gtag) {
    window.gtag('event', 'generate_lead', {
      event_category: 'conversion',
      event_label: service || 'eligibility',
    });
    console.log('[Analytics] GA4 generate_lead event');
  }

  // Meta Pixel Lead event
  if (isMetaPixelEnabled() && window.fbq) {
    window.fbq('track', 'Lead', eventData);
    console.log('[Analytics] Meta Lead event');
  }
};

/**
 * Track custom event
 * @param {string} eventName - Event name
 * @param {Object} params - Event parameters (NO PII)
 */
export const trackEvent = (eventName, params = {}) => {
  if (!hasAcceptedTracking()) return;

  // GA4 custom event
  if (isGA4Enabled() && window.gtag) {
    window.gtag('event', eventName, params);
  }

  // Meta Pixel custom event
  if (isMetaPixelEnabled() && window.fbq) {
    window.fbq('trackCustom', eventName, params);
  }
};

export default {
  initializeAnalytics,
  initializeGA4,
  initializeMetaPixel,
  trackPageView,
  trackLead,
  trackEvent,
};
